import { InputLabel } from "@material-ui/core";
import classNames from "classnames";
import { compose } from "ramda";
import { memo, useEffect, useState } from "react";
import { Control, Controller, UseFormMethods } from "react-hook-form";
import PhoneInput from "react-phone-input-2";
import { useSelector } from "react-redux";

import { countriesDialCodes as countriesCode } from "../../shared";
import { RootState } from "../../store";
import { decodeComponentId } from "../../utilities/forms";
import { withError } from "../HOC";
import { CommonFormElProps } from "../types";

import "react-phone-input-2/lib/plain.css";
import styles from "./Phone.module.scss";

export type PhoneProps = {
  readonly control: Control;
  readonly country: (state: RootState) => string;
  readonly setValue: UseFormMethods["setValue"];
  readonly trigger: UseFormMethods["trigger"];
  readonly defaultValue?: string;
} & CommonFormElProps;

export const Phone = memo<PhoneProps>(
  ({
    control,
    country: countrySelector,
    defaultValue,
    hasError,
    id,
    label,
    onChange: onChangeAction,
    required = false,
    setValue,
    trigger,
  }) => {
    const inputClasses = classNames(styles.input, {
      [styles.error]: hasError,
    });
    const buttonClasses = classNames(styles.button, {
      [styles.error]: hasError,
    });
    const countryFromStore = useSelector(countrySelector);
    const [country, setCountry] = useState<string>(countryFromStore);
    const { formState, updateFormState } = control;

    useEffect(() => {
      onChangeAction({
        field: decodeComponentId(id),
        value: { country, isActive: true },
      });
    }, [country, onChangeAction, id]);
    useEffect(() => {
      return () => {
        onChangeAction({
          field: decodeComponentId(id),
          value: { isActive: false },
        });
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
      setValue(id, defaultValue);
    }, [defaultValue, id, setValue]);

    return (
      <Controller
        control={control}
        defaultValue={defaultValue}
        name={id}
        render={({ onChange }) => (
          <>
            <InputLabel htmlFor={id} required={required}>
              {label}
            </InputLabel>
            <PhoneInput
              autoFormat={false}
              enableLongNumbers
              alwaysDefaultMask
              defaultMask={"_______________"}
              buttonClass={buttonClasses}
              country={country}
              containerClass={styles.container}
              dropdownClass={styles.dropdown}
              dropdownStyle={{ zIndex: 1235 }}
              inputClass={inputClasses}
              copyNumbersOnly={false}
              inputProps={{ id, name: id, required, "aria-invalid": hasError }}
              onChange={(v, data: any) => {
                onChange(`+${v}`);
                setCountry(data.countryCode);
              }}
              onBlur={() => {
                updateFormState({
                  touched: {
                    ...JSON.parse(JSON.stringify(formState.touched)),
                    [id]: true,
                  },
                });
                trigger(id);
              }}
              onlyCountries={countriesCode}
              placeholder="+48"
              value={defaultValue as string}
            />
          </>
        )}
      />
    );
  }
);

export const PhoneWithError = compose(withError)(Phone);
